import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import PropTypes from 'prop-types';
import H4 from '@base/H4/H4';
import H7 from '@base/H7/H7';
import LinearProgressBar from '@base/LinearProgressBar/LinearProgressBar';
import PlayCircleBig from '@icon/PlayCircleBig';
import CheckBig from '@icon/CheckBig';
import { getImage } from '@utils/imageHelper';
import dateHelper from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { sessionStates } from '@constants/index';
import Item from '@models/Item';
import { Fonts, Spacings } from '@styles/index';

const ShadowPulse = keyframes`
 0% { box-shadow: ${boxShadowGenerator({
   yOffset: Spacings.SPACING_0B,
   blur: Spacings.SPACING_0B,
   color: 'rgba(235,87,87, 0.4)'
 })}; }
 70% { box-shadow: ${boxShadowGenerator({
   yOffset: Spacings.SPACING_0B,
   blur: Spacings.SPACING_0B,
   spread: '10px',
   color: 'rgba(235,87,87, 0)'
 })}; }
 100% {
   box-shadow: none;
 }
`;

const Play = styled(PlayCircleBig)`
  margin-right: ${Spacings.SPACING_6B};
`;

const Live = styled(PlayCircleBig)`
  margin-right: ${Spacings.SPACING_6B};
  border-radius: 50%;
  box-shadow: ${boxShadowGenerator({
    yOffset: Spacings.SPACING_0B,
    blur: Spacings.SPACING_0B,
    color: 'rgba(235,87,87, 0.4)'
  })};
  animation: ${ShadowPulse} 2s infinite;
  :hover {
    animation: none;
  }
`;

const OfflineCentreClassImage = styled.img`
  height: 40px;
  width: 40px;
`;

const LiveQuiz = styled.img`
  margin-right: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  border-radius: 50%;
  user-drag: none;
  user-select: none;
  box-shadow: ${boxShadowGenerator({
    yOffset: Spacings.SPACING_0B,
    blur: Spacings.SPACING_0B,
    color: 'rgba(235,87,87, 0.4)'
  })};
  animation: ${ShadowPulse} 2s infinite;
  :hover {
    animation: none;
  }
`;

const DoneCircle = styled(CheckBig)`
  margin-right: ${Spacings.SPACING_6B};
`;

const Quiz = styled.img`
  margin-right: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  user-drag: none;
  user-select: none;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_4B : Spacings.SPACING_6B};
  width: ${Spacings.SPACING_10B};
  @media only screen and (max-width: 47.95em) {
    margin-right: ${Spacings.SPACING_4B};
    height: ${({ $isRecordedBatchLesson }) =>
      $isRecordedBatchLesson ? 'unset' : '47px'};
  }
`;

const Month = styled(H7)`
  color: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? 'var(--color-i-gray-13)' : ''};
  font-size: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Fonts.EXTRA_SMALL : ''};
  width: ${Spacings.SPACING_10B};
  text-align: center;
  text-transform: uppercase;
`;

const MonthDateWrapper = styled.div`
  background-color: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? 'var(--color-i-violet-dark-1)' : ''};
  border-radius: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_2B : ''};
  padding-top: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_5 : ''};
  padding-bottom: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_5 : ''};
`;

const Day = styled(H4)`
  text-align: center;
  color: var(--color-text-primary);
  font-size: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Fonts.H6 : ''};
  margin-top: ${({ $isRecordedBatchLesson }) =>
    $isRecordedBatchLesson ? Spacings.SPACING_1B : 0};
  @media only screen and (max-width: 47.95em) {
    font-size: ${({ $isRecordedBatchLesson }) =>
      $isRecordedBatchLesson ? Fonts.H6 : '18px'};
  }
`;

const StyLinearProgressBar = styled(LinearProgressBar)`
  margin-top: ${Spacings.SPACING_3B};
`;

const quizState = (lesson) => {
  const currentDate = new Date();
  const { duration } = lesson;
  const endDate = new Date(lesson.startsAt);
  endDate.setSeconds(endDate.getSeconds() + duration);
  const val = dateHelper.compareDates(endDate, currentDate);
  if (val === -1) return sessionStates.FUTURE;
  if (val === 1) return sessionStates.PAST;
};

const renderLeft = ({ lesson, showDate, isSubscribed, isLessonLive }) => {
  const {
    state,
    liveAt,
    hasWatched,
    lessonDuration,
    watchTime,
    type,
    latestSession,
    startsAt,
    secondsBeforeLive
  } = lesson;

  const { isOffline = false, isLiveStreamingEnabled = true } =
    lesson.properties || lesson;
  const isStreamingDisabled = isOffline && !isLiveStreamingEnabled;
  const playButtonColor = 'var(--color-i-green)';
  const live = liveAt ? new Date(liveAt) : new Date(startsAt);

  if (isLessonLive && Item.isPost(lesson) && isStreamingDisabled)
    return (
      <OfflineCentreClassImage
        src={getImage('offline-centre-live-class.svg')}
        alt=""
      />
    );
  if (isLessonLive && Item.isPost(lesson))
    return <Live size="40px" color="var(--color-i-red)" />;
  if (isLessonLive && !Item.isPost(lesson))
    return <LiveQuiz src={getImage('liveTests.svg')} alt="quiz" />;

  if (
    showDate ||
    (type === 'post' &&
      state === sessionStates.FUTURE &&
      secondsBeforeLive > 900) ||
    (type === 'quiz' && quizState(lesson) === sessionStates.FUTURE) ||
    Item.isCompletedWithNoVideoUrl(lesson) ||
    Item.classDidNotHappen(lesson) ||
    !isSubscribed
  ) {
    return (
      <>
        <MonthDateWrapper>
          <Month>
            {live ? live.toLocaleString('default', { month: 'short' }) : ''}
          </Month>
          <Day>{live ? live?.getDate() : ''}</Day>
        </MonthDateWrapper>
      </>
    );
  }

  if (type === 'quiz' && latestSession)
    return <DoneCircle size="40px" color="var(--color-i-orange-1)" />;
  if (type === 'quiz' && quizState(lesson) === sessionStates.PAST) {
    return <Quiz src={getImage('quiz.png')} alt="quiz" />;
  }

  if (hasWatched)
    return <DoneCircle size="40px" color="var(--color-i-green)" />;

  return (
    <>
      <Play size="40px" color={playButtonColor} />
      {watchTime ? (
        <StyLinearProgressBar
          value={Math.max((watchTime * 100) / lessonDuration, 10)}
          width="40px"
          height="3px"
          backgroundColor="var(--color-divider)"
          overlayColor={playButtonColor}
        />
      ) : null}
    </>
  );
};

const LeftIconV2 = ({ lesson, showDate, isSubscribed, liveClassList }) => {
  const isLessonLive = liveClassList[lesson.uid];
  return (
    <Wrapper>
      {renderLeft({
        lesson,
        showDate,
        isSubscribed,
        isLessonLive
      })}
    </Wrapper>
  );
};

export default LeftIconV2;

LeftIconV2.propTypes = {
  lesson: PropTypes.objectOf(PropTypes.any).isRequired,
  showDate: PropTypes.bool,
  liveClassList: PropTypes.objectOf(PropTypes.any)
};

LeftIconV2.defaultProps = {
  showDate: false,
  liveClassList: {}
};
